'use client'

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { commentFormSchema, type CommentFormInput } from '@/lib/validations/comment.schema'

interface CommentFormProps {
  onSubmit: (content: string) => Promise<void>
  disabled?: boolean
  parentCommentId?: string
  avatarUrl?: string | null
  username?: string
  initialExpanded?: boolean
  onCancel?: () => void
}

export function CommentForm({ onSubmit, disabled = false, avatarUrl, username, initialExpanded = false, onCancel }: CommentFormProps) {
  const [expanded, setExpanded] = useState(initialExpanded)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CommentFormInput>({
    resolver: zodResolver(commentFormSchema),
    defaultValues: { content: '' },
  })

  const content = watch('content')
  const charCount = content?.length ?? 0

  const handleFormSubmit = handleSubmit(async (data) => {
    await onSubmit(data.content)
    reset()
    setExpanded(false)
  })

  const handleCancel = () => {
    reset()
    setExpanded(false)
    onCancel?.()
  }

  const avatarInitial = username ? username.charAt(0).toUpperCase() : '?'

  // Collapsed pill state
  if (!expanded) {
    return (
      <div className="mb-4 flex items-center gap-3">
        <div className="h-8 w-8 shrink-0 rounded-full bg-[var(--color-bg-elevated)]" aria-hidden="true">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-brand-indigo)]/20 text-[var(--text-xs)] font-medium text-[var(--color-brand-indigo)]">
              {avatarInitial}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => !disabled && setExpanded(true)}
          disabled={disabled}
          className="flex-1 rounded-full border border-[var(--color-border-default)] bg-[var(--color-bg-primary)] px-4 py-2 text-left text-[var(--text-sm)] text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-brand-indigo)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-indigo)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Add a comment...
        </button>
      </div>
    )
  }

  // Expanded state
  return (
    <div className="mb-4 flex gap-3">
      <div className="h-8 w-8 shrink-0 rounded-full bg-[var(--color-bg-elevated)]" aria-hidden="true">
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-brand-indigo)]/20 text-[var(--text-xs)] font-medium text-[var(--color-brand-indigo)]">
            {avatarInitial}
          </div>
        )}
      </div>
      <form onSubmit={handleFormSubmit} className="min-w-0 flex-1">
        <div className="rounded-[var(--radius-lg)] border border-[var(--color-border-default)] bg-[var(--color-bg-primary)] p-3 transition-colors focus-within:border-[var(--color-brand-indigo)]">
          <textarea
            {...register('content')}
            placeholder="Add a comment..."
            rows={3}
            maxLength={500}
            disabled={disabled || isSubmitting}
            autoFocus
            aria-label="Comment"
            aria-describedby={errors.content ? 'comment-error' : 'comment-hint'}
            aria-invalid={!!errors.content}
            className="w-full resize-y border-0 bg-transparent p-0 text-[var(--text-sm)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          />
          <div className="mt-2 flex items-center justify-between">
            <span
              className={`text-[var(--text-xs)] tabular-nums ${
                charCount > 450 ? 'text-[var(--color-error)]' : 'text-[var(--color-text-muted)]'
              }`}
              aria-hidden="true"
            >
              {charCount}/500
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCancel}
                disabled={disabled || isSubmitting}
                className="rounded-[var(--radius-md)] px-3 py-1.5 text-[var(--text-sm)] text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-indigo)] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={disabled || isSubmitting || charCount === 0}
                className="rounded-full bg-[var(--color-brand-indigo)] px-4 py-1.5 text-[var(--text-sm)] font-medium text-white transition-colors hover:bg-[var(--color-brand-violet)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-indigo)] disabled:opacity-50"
              >
                {isSubmitting ? 'Posting...' : 'Post'}
              </button>
            </div>
          </div>
        </div>

        {errors.content && (
          <p id="comment-error" className="mt-1 text-[var(--text-xs)] text-[var(--color-error)]" role="alert">
            {errors.content.message}
          </p>
        )}

        <p id="comment-hint" className="sr-only">
          Maximum 500 characters
        </p>
      </form>
    </div>
  )
}
